import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, inject } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import {
  BehaviorSubject,
  EMPTY,
  catchError,
  finalize,
  first,
  tap,
} from 'rxjs';
import { Constants } from 'src/app/core/constants';
import { CURRENCY_SELECT_OPTIONS } from 'src/app/core/constants/currency-options.constant';
import { InventoryItemCurrencies } from 'src/app/core/enums/inventory-item-currencies.enum';
import { InventoryItem } from 'src/app/core/models/inventory/inventory-item.interface';
import { InventoryItemOverviewFormModel } from 'src/app/core/models/inventory/inventory-item-overview-form.model';
import { CoreModule } from 'src/app/core/core.module';
import { InventoryItemService } from 'src/app/core/services/inventory-item.service';
import { isConcurrencyConflictError } from 'src/app/core/utils/api-error.util';
import {
  InventoryItemFinanceEditableFieldName,
  InventoryItemFinancePreviewUpdater,
} from './inventory-item-finances-tab.type';
import { InventoryItemFinanceViewModel } from './inventory-item-finances-tab.view-model';

@Component({
  selector: 'app-inventory-item-finances-tab',
  templateUrl: './inventory-item-finances-tab.component.html',
  styleUrl: './inventory-item-finances-tab.component.scss',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, CoreModule],
  providers: [InventoryItemOverviewFormModel],
})
export class InventoryItemFinancesTabComponent implements OnChanges {
  readonly overviewForm = inject(InventoryItemOverviewFormModel);
  private readonly inventoryItemService = inject(InventoryItemService);

  @Input({ required: true }) public companyId = '';
  @Input({ required: true }) public itemId = '';
  @Input() public reloadToken = '';
  @Input() public inventoryItem: InventoryItem | null = null;
  @Output() public readonly inventoryItemChange = new EventEmitter<InventoryItem>();
  @Output() public readonly concurrencyConflict = new EventEmitter<void>();

  public readonly notFound = Constants.NotFound;
  public readonly inventoryItem$ = new BehaviorSubject<InventoryItem | null>(null);
  public readonly viewModel$ = new BehaviorSubject<InventoryItemFinanceViewModel | null>(null);
  public isLoading = false;

  private readonly currencySymbols: Record<InventoryItemCurrencies, string> = {
    [InventoryItemCurrencies.UAH]: '₴',
    [InventoryItemCurrencies.EUR]: '€',
    [InventoryItemCurrencies.USD]: '$',
  };

  private readonly currencyOptions = CURRENCY_SELECT_OPTIONS;

  private readonly patchUpdaters: Record<
    InventoryItemFinanceEditableFieldName,
    InventoryItemFinancePreviewUpdater
  > = {
    unitPrice: (_, value) => ({ unitPrice: this.toNumber(value) }),
    unitCost: (_, value) => ({ unitCost: this.toNumber(value) }),
    currencyCode: (_, value) => ({ currencyCode: this.resolveCurrencyCode(typeof value === 'string' ? value : null) }),
    priceIncludesTax: (_, value) => ({ priceIncludesTax: Boolean(value) }),
  };

  public ngOnChanges(changes: SimpleChanges): void {
    if (!changes['companyId'] && !changes['itemId'] && !changes['reloadToken'] && !changes['inventoryItem']) {
      return;
    }

    if (this.inventoryItem) {
      this.syncInventoryItemState(this.inventoryItem);
      return;
    }

    this.loadInventoryItem();
  }

  public onPreviewFieldUpdate(fieldName: InventoryItemFinanceEditableFieldName): void {
    const currentItem = this.inventoryItem$.getValue();
    if (!currentItem) {
      return;
    }

    const rawValue = this.overviewForm.form.controls[fieldName].value;
    const normalizedValue = typeof rawValue === 'string' ? rawValue.trim() : rawValue;
    const updater = this.patchUpdaters[fieldName];
    const changes = this.withConcurrencyMetadata(currentItem, updater(currentItem, normalizedValue));

    this.inventoryItemService
      .updateInventoryItemFinance(this.companyId, this.itemId, changes)
      .pipe(
        first(),
        tap((inventoryItem) => {
          this.syncInventoryItemState(inventoryItem, this.getFieldLabelKey(fieldName));
          this.inventoryItemChange.emit(inventoryItem);
        }),
        catchError((error) => {
          this.syncInventoryItemState(currentItem);
          if (isConcurrencyConflictError(error)) {
            this.concurrencyConflict.emit();
          }
          return EMPTY;
        }),
      )
      .subscribe();
  }

  private loadInventoryItem(): void {
    if (!this.companyId || !this.itemId) {
      return;
    }

    this.isLoading = true;
    this.inventoryItemService
      .getInventoryItem(this.companyId, this.itemId)
      .pipe(
        first(),
        tap((inventoryItem) => this.syncInventoryItemState(inventoryItem)),
        catchError(() => EMPTY),
        finalize(() => {
          this.isLoading = false;
        }),
      )
      .subscribe();
  }

  private syncInventoryItemState(
    inventoryItem: InventoryItem,
    previewFieldLabelKey?: string,
  ): void {
    const currencyCode = this.resolveCurrencyCode(inventoryItem.currencyCode);

    this.inventoryItem$.next(inventoryItem);
    this.overviewForm.form.patchValue(
      {
        unitPrice: inventoryItem.unitPrice.toString(),
        unitCost: inventoryItem.unitCost.toString(),
        currencyCode,
        marginAmount: inventoryItem.marginAmount.toString(),
        marginPercentage: inventoryItem.marginPercentage.toString(),
        priceIncludesTax: inventoryItem.priceIncludesTax,
      },
      { emitEvent: false },
    );
    this.viewModel$.next(this.buildViewModel(inventoryItem, previewFieldLabelKey));
  }

  private buildViewModel(
    item: InventoryItem,
    previewFieldLabelKey?: string,
  ): InventoryItemFinanceViewModel {
    const currencyCode = this.resolveCurrencyCode(item.currencyCode);
    const isEditable = item.isArchived !== true;

    return {
      previewFieldLabelKey,
      salesPriceField: {
        controlName: 'unitPrice',
        label: 'COMPONENTS.GOODS.FIELDS.SALES_PRICE',
        inputType: 'number',
        value: this.formatCurrencyAmount(item.unitPrice, currencyCode),
        valueClass: 'inventory-item-finances-tab__value inventory-item-finances-tab__value--sales',
        editable: isEditable,
      },
      costField: {
        controlName: 'unitCost',
        label: 'COMPONENTS.GOODS.FIELDS.COST',
        inputType: 'number',
        value: this.formatCurrencyAmount(item.unitCost, currencyCode),
        valueClass: 'inventory-item-finances-tab__value',
        editable: isEditable,
      },
      currencyField: {
        controlName: 'currencyCode',
        label: 'COMPONENTS.GOODS.FIELDS.CURRENCY',
        inputType: 'select',
        value: currencyCode,
        valueKey: `INVENTORY_ITEM_CURRENCIES.${currencyCode}`,
        valueClass: 'inventory-item-finances-tab__value inventory-item-finances-tab__value--mono',
        enumName: 'INVENTORY_ITEM_CURRENCIES',
        enumObject: InventoryItemCurrencies,
        // Spread copy so the shared (readonly) constant doesn't leak into the
        // mutable `selectItems: InventoryItemFinanceSelectItem[]` contract.
        selectItems: [...this.currencyOptions],
        editable: isEditable,
      },
      priceIncludesVatField: {
        controlName: 'priceIncludesTax',
        label: 'COMPONENTS.GOODS.FIELDS.PRICE_INCLUDES_VAT',
        inputType: 'custom',
        value: item.priceIncludesTax ? 'COMMON.YES' : 'COMMON.NO',
        valueKey: item.priceIncludesTax ? 'COMMON.YES' : 'COMMON.NO',
        booleanValue: item.priceIncludesTax,
        editable: isEditable,
      },
      marginDisplay: {
        label: 'COMPONENTS.GOODS.FIELDS.MARGIN',
        percentage: this.formatPercentage(item.marginPercentage),
        amount: this.formatCurrencyAmount(item.marginAmount, currencyCode),
        percentageClass: this.getMarginPercentageClass(item.marginPercentage),
      },
    };
  }

  private formatPercentage(value: number): string {
    const formattedValue = new Intl.NumberFormat(undefined, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(value);

    return `${formattedValue}%`;
  }

  private formatCurrencyAmount(value: number, currencyCode: InventoryItemCurrencies): string {
    const formattedValue = new Intl.NumberFormat(undefined, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(value);

    return `${formattedValue} ${this.currencySymbols[currencyCode]}`;
  }

  private getMarginPercentageClass(value: number): string | undefined {
    if (value < 0) {
      return 'inventory-item-finances-tab__margin-percentage inventory-item-finances-tab__margin-percentage--negative';
    }

    if (value > 0) {
      return 'inventory-item-finances-tab__margin-percentage inventory-item-finances-tab__margin-percentage--positive';
    }

    return 'inventory-item-finances-tab__margin-percentage';
  }

  private resolveCurrencyCode(value?: string | null): InventoryItemCurrencies {
    if (value && Object.values(InventoryItemCurrencies).includes(value as InventoryItemCurrencies)) {
      return value as InventoryItemCurrencies;
    }

    return InventoryItemCurrencies.EUR;
  }

  private getFieldLabelKey(fieldName: InventoryItemFinanceEditableFieldName): string {
    const labels: Record<InventoryItemFinanceEditableFieldName, string> = {
      unitPrice: 'COMPONENTS.GOODS.FIELDS.SALES_PRICE',
      unitCost: 'COMPONENTS.GOODS.FIELDS.COST',
      currencyCode: 'COMPONENTS.GOODS.FIELDS.CURRENCY',
      priceIncludesTax: 'COMPONENTS.GOODS.FIELDS.PRICE_INCLUDES_VAT',
    };

    return labels[fieldName];
  }

  private toNumber(value: string | number | boolean | null | undefined): number {
    if (typeof value === 'number') {
      return Number.isFinite(value) ? value : 0;
    }

    if (typeof value !== 'string') {
      return 0;
    }

    const parsedValue = Number.parseFloat(value);
    return Number.isFinite(parsedValue) ? parsedValue : 0;
  }

  private withConcurrencyMetadata<T extends object>(
    inventoryItem: InventoryItem,
    changes: T,
  ): T & { expectedConcurrencyToken: string } {
    return {
      ...changes,
      expectedConcurrencyToken: inventoryItem.concurrencyToken ?? '',
    };
  }
}
